import { FC, useMemo, useState } from "react"
import { Box, Button, Tooltip } from "@mui/material"
import { Add } from "@mui/icons-material"
import DeleteOutlined from "@mui/icons-material/DeleteOutlined"
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro"
import { DataGrid, Modal, SnackbarAlert } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { GuestBot } from "../../interfaces"
import { useBindingsListQuery, useDeleteResponseMutation } from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import { BindingForm } from "../forms"
import { TooltipAction } from "../common"

export const BindingsTable: FC = () => {
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const { data, isFetching } = useBindingsListQuery({
    queryParams: { limit: pageSize, offset: page * pageSize },
  })
  const rows = data?.items ?? []
  const canWrite = useGuestBotWriteAccess()
  const [deleteResponse] = useDeleteResponseMutation()

  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const columns: GridColDef<GuestBot.GuestBotResponse>[] = useMemo(
    () => [
      {
        field: "bot",
        headerName: "Бот",
        flex: 2,
        valueGetter: (params) =>
          params.row.bot
            ? (params.row.bot.username ?? String(params.row.bot.botId))
            : "(бот удалён)",
      },
      {
        field: "triggerText",
        headerName: "Триггер",
        flex: 2,
        valueGetter: (params) => params.row.triggerText ?? "(fallback)",
      },
      {
        field: "template",
        headerName: "Шаблон",
        flex: 3,
        valueGetter: (params) => params.row.template?.name ?? "(шаблон удалён)",
      },
    ],
    [],
  )

  const rowActions = useMemo(() => {
    if (!canWrite) return []
    return [
      (params: GridRowParams<GuestBot.GuestBotResponse>) => (
        <TooltipAction
          icon={<DeleteOutlined />}
          label={
            params.row.bot ? "Удалить" : "Нельзя удалить: бот привязки удалён"
          }
          disabled={!params.row.bot}
          onClick={async () => {
            if (!params.row.bot) return
            const res = await deleteResponse({
              pathParams: {
                id: params.row.bot.id,
                responseId: params.row.id,
              },
            })
            if ("error" in res) {
              setError("Не удалось удалить привязку")
            }
          }}
        />
      ),
    ]
  }, [canWrite, deleteResponse])

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Tooltip title="Добавить привязку бота к шаблону" arrow>
            <Button startIcon={<Add />} onClick={() => setCreating(true)}>
              Добавить привязку
            </Button>
          </Tooltip>
        </Box>
      )}
      <Box height="70vh">
        <DataGrid<GuestBot.GuestBotResponse>
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={isFetching}
          paginationMode="server"
          rowCount={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          rowActions={rowActions}
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новая привязка"
          onClose={() => setCreating(false)}
          element={<BindingForm onDone={() => setCreating(false)} />}
        />
      )}
      <SnackbarAlert
        isOpen={!!error}
        onClose={() => setError(null)}
        variant="error"
        message={error ?? ""}
      />
    </>
  )
}
